import { OnInit, inject, OnDestroy, Directive } from '@angular/core';
import { Router } from '@angular/router';
import { GameBuilderService } from '../../../../core/services/game-builder.service';
import { createAIGameJobRequest } from '../../../../core/models/request.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SocketService } from '../../../../core/services/socket.service';
import { generateIdempotencyKey } from '../../../../core/helpers/quizai.helper';
import { GameTemplate } from '../../../../core/models/game.model';
import { Subject, Subscription } from 'rxjs';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslationService } from '../../../../core/services/translation.service';
import { TEXT_LENGTH_LIMIT } from '../../../../shared/constants/texts';
import { Observable, catchError, EMPTY, filter, switchMap, take, takeUntil, tap, finalize } from 'rxjs';
import { Store } from '@ngrx/store';
import { AuthState, AIJob, AIJobSocketResponse, Balance } from '../../../../core/models/auth.model';
import * as AuthActions from '../../../../core/store/auth/auth.actions';
import { StartGameHelper } from '../../../../core/helpers/start-game.helper';
import { GameService } from '../../../../core/services/game.service';
import { selectAIJob, selectBalance } from '../../../../core/store/auth/auth.selectors';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../../../../environments/environment';
import { PRICE } from '../../../../shared/constants/tokens';

export const AI_GAME_TYPE = 'quiz-ai';

export const AI_GAME_STATUS = {
  QUEUED: 'queued',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELED: 'canceled'
}

@Directive()
export abstract class QuizAIBaseComponent implements OnInit, OnDestroy {
  private jobId: number | null = null;
  private destroy$ = new Subject<void>();
  private _formBuilder = inject(FormBuilder);
  private sub = new Subscription();
  private aiJob: AIJob | null = null;
  private readonly DEBUG = environment.debugLog;
  public currentBalance$!: Observable<Balance | null>;
  // protected readonly descriptionValue = signal('');
  public balance!: Balance | null;
  public createGamePrice = PRICE.CREATE_GAME_COST;
  public loading: boolean = true;
  public currentLang: string = '';
  public gameTemplates: GameTemplate[] = [];
  public allLanguages: { code: string, name: string, short: string, image: string }[] = [];
  public TEXT_LENGTH_LIMIT = TEXT_LENGTH_LIMIT;
  public taskForm: FormGroup;
  public difficulties: string[] = ['easy', 'medium', 'hard', 'expert'];
  public started: boolean = false;
  public inQueue: boolean | null = null;
  public queuePosition: number = 0;
  public queueEta: number = 0;
  public queueSummary: string = '';
  public quizStatus: string | null = null;
  public quizMsg: string = '';
  public completedSuccess: boolean | null = null;
  public gameLoading: boolean = false;
  public currentAIJob$: Observable<AIJob | null>;
  public errorDetails: {
    error?: string;
    reason?: string;
    suggestion?: string;
  } | null = null;
  private translations: Record<string, string> = {};

  constructor(
    private gameBuilderService: GameBuilderService,
    private snackBar: MatSnackBar,
    private socketService: SocketService,
    private translationService: TranslationService,
    private store: Store<{ auth: AuthState }>,
    private router: Router,
    private startGameHelper: StartGameHelper,
    private gameService: GameService,
    private dialog: MatDialog,
  ) {
    this.allLanguages = this.translationService.getAllLanguages();
    this.taskForm = this._formBuilder.group({
      lang: new FormControl('', Validators.required),
      template: new FormControl('', Validators.required),
      difficulty: new FormControl('', Validators.required),
      topic: new FormControl('', [Validators.required, Validators.minLength(this.TEXT_LENGTH_LIMIT.QUIZAI_TOPIC.MIN), Validators.maxLength(this.TEXT_LENGTH_LIMIT.QUIZAI_TOPIC.MAX)]),
      description: new FormControl('', [Validators.minLength(this.TEXT_LENGTH_LIMIT.QUIZAI_DESCRIPTION.MIN), Validators.maxLength(this.TEXT_LENGTH_LIMIT.QUIZAI_DESCRIPTION.MAX)]),
    });

    this.currentAIJob$ = this.store.select(selectAIJob);
    this.currentAIJob$.pipe(takeUntil(this.destroy$)).subscribe(aiJob => {
      this.loading = false;

      if (aiJob) {
        this.aiJob = aiJob;
        this.initState();
        this.debugLog('initState aiJob', aiJob);
      }
    });
  }

  ngOnInit() {
    this.translationService.getCurrentLang().pipe(takeUntil(this.destroy$)).subscribe(lang => {
      this.currentLang = lang;
      this.taskForm.patchValue({
        lang
      });
      this.translationService.translateBatch([
      'game_tokens.insufficient_tokens.title',
      'game_tokens.insufficient_tokens.create_message',
      'game_tokens.insufficient_tokens.confirmText',
      'game_tokens.insufficient_tokens.cancelText',
      'quizai.cancel.title',
      'quizai.cancel.message',
      'quizai.cancel.snackbar',
      'quizai.cancel.requested'
    ]).pipe(take(1)).subscribe(([
      insufficientTokensTitle,
      insufficientTokensCreateMessage,
      insufficientTokensConfirmText,
      insufficientTokensCancelText,
      cancelTitle,
      cancelMessage,
      cancelSnackbar,
      cancelRequested
    ]) => {
      this.translations = {
        insufficientTokensTitle,
        insufficientTokensCreateMessage,
        insufficientTokensConfirmText,
        insufficientTokensCancelText,
        cancelTitle,
        cancelMessage,
        cancelSnackbar,
        cancelRequested
      };
    });
    });

    this.gameBuilderService.getGameTemplates().pipe(takeUntil(this.destroy$)).subscribe((templates: GameTemplate[]) => {
      this.gameTemplates = templates;
      if (this.aiJob?.templateId) {
        this.taskForm.patchValue({
          template: this.findTemplate(this.aiJob.templateId)
        })
      }
    });

    this.currentBalance$ = this.store.select(selectBalance);
    this.currentBalance$.pipe(takeUntil(this.destroy$)).subscribe(balance => {
      this.balance = balance;
    });

    // receive updates globally
    this.socketService.onSticky$<{ jobId: number; position: number; queued: number; running: number; concurrency: number; etaSec: number }>('aiq:job.queue')
      .pipe(filter(p => p.jobId === this.jobId), takeUntil(this.destroy$))
      .subscribe(p => {
        this.queuePosition = p.position;      // 1,2,3…
        this.queueEta = p.etaSec;             // seconds
        this.queueSummary = `#${p.position} in queue • ~${Math.max(0, p.etaSec)}s`;
        // console.log('aiq:job.queue:: queuePosition:', this.queuePosition, ', queueEta:', this.queueEta, ', queueSummary:', this.queueSummary);
      });
    this.sub.add(this.socketService.aiJobStarted$.subscribe(p => {
      this.updateStatus(p);
      // console.log('aiJobStarted', p)
    }));
    this.sub.add(this.socketService.aiJobProgress$.subscribe(p => {
      this.updateStatus(p);
      // console.log('aiJobProgress', p)
    }));
    this.sub.add(this.socketService.aiJobCompleted$.subscribe(p => {
      this.updateStatus(p);
      // console.log('aiJobCompleted', p)
    }));
    this.sub.add(this.socketService.aiJobFailed$.subscribe(p => {
      this.updateStatus(p);
      // console.log('aiJobFailed', p)
    }));
  }

  private showInsufficientTokensDialog() {
    this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: this.translations['insufficientTokensTitle'],
        message: this.translations['insufficientTokensCreateMessage'],
        confirmText: this.translations['insufficientTokensConfirmText'],
        cancelText: this.translations['insufficientTokensCancelText']
      },
    }).afterClosed().pipe(take(1)).subscribe((confirmed) => {
      if (confirmed) {
        this.router.navigate(['/shop']);
      }
    });
  }

  public genQuiz() {
    if (!this.balance || this.balance.total < this.createGamePrice) {
      this.showInsufficientTokensDialog();
      return;
    }

    this.started = true;
    const testJob: createAIGameJobRequest = {
      type: AI_GAME_TYPE,
      topic: this.taskForm.value.topic,
      description: this.taskForm.value.description,
      idempotencyKey: generateIdempotencyKey(),
      templateId: this.taskForm.value.template.id,
      difficulty: this.taskForm.value.difficulty,
      lang: this.taskForm.value.lang
    }
    this.gameBuilderService.createAIGameJob(testJob).subscribe({
      next: (result) => {
        this.debugLog('SUCCESS', result);
        this.jobId = result.jobId;
        this.socketService.trackJobQueue(this.jobId);
      },
      error: (error) => {
        this.debugLog('Error creating AI game:', error);
        this.snackBar.open(
          error.error?.message?? 'Something went wrong',
          'Close',
          { duration: 3000, panelClass: 'errorNotificationMessage' }
        );
      }
    });
  }

  cancelGen() {
    const id = this.jobId;

    if (id) {
      this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: this.translations['cancelTitle'],
          message: this.translations['cancelMessage']
        }
      }).afterClosed().pipe(
        take(1),
        filter((confirmed: boolean) => confirmed),
        switchMap(() =>
          this.gameBuilderService.cancelAIGameJob(id).pipe(
            tap((resp: { ok: boolean, already: boolean }) => {
              this.debugLog('SUCCESS', resp);
              this.completedSuccess = false;
              if (resp.ok && resp.already) {
                this.snackBar.open(
                  this.translations['cancelSnackbar'],
                  'Close',
                  { duration: 3000 }
                );

                this.clearState();
              }
              if (resp.ok && !resp.already) {
                this.quizStatus = AI_GAME_STATUS.CANCELED;
                this.quizMsg = this.translations['cancelRequested'];
              }
            }),
            catchError((error) => {
              this.debugLog('Error canceling generation', error);
              this.snackBar.open(
                error.error?.message?? 'Something went wrong',
                'Close',
                { duration: 3000, panelClass: 'errorNotificationMessage' }
              );
              return EMPTY;
            })
          )
        ),
        takeUntil(this.destroy$)
      ).subscribe();
    }
  }

  restartGen() {
    this.confirmJob();
    this.clearState();
  }

  startGame(): void {
    if (this.gameLoading || !this.aiJob?.quizId) return;

    this.gameLoading = true;
    this.confirmJob();
    this.startGameHelper.startGame(this.aiJob.quizId).pipe(
      finalize(() => this.gameLoading = false),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  editGame() {
    if (!this.aiJob?.quizId) return;

    this.confirmJob();
    this.gameService.getGameSlugInfo(this.aiJob.quizId).subscribe({
      next: (result) => {
        this.debugLog('SUCCESS', result);
        this.router.navigate(['/profile/my-game', result.slug, result.lang]);
      },
      error: (error) => {
        this.debugLog('Error getting game slug:', error);
        this.snackBar.open(
          error.error?.message?? 'Something went wrong',
          'Close',
          { duration: 3000, panelClass: 'errorNotificationMessage' }
        );
      }
    });
  }

  goToGen() {
    this.router.navigate([this.currentLang + '/profile/quiz-ai']);
  }

  close() {
    this.confirmJob();
    this.clearState();
  }

  updateStatus(p: AIJobSocketResponse) {
    this.debugLog('updateStatus', p);
    if (p.errorDetail) {
      this.errorDetails = JSON.parse(p.errorDetail);
      this.debugLog('errorDetails', this.errorDetails);
    }
    this.quizStatus = p.status;
    this.inQueue = p.status === AI_GAME_STATUS.QUEUED;
    this.quizMsg = p.msg;
    if (!this.inQueue) {
      this.socketService.untrackJobQueue(this.jobId);
    }
    this.completedSuccess = p.status === AI_GAME_STATUS.COMPLETED ? true : [AI_GAME_STATUS.FAILED, AI_GAME_STATUS.CANCELED].includes(p.status) ? false : null;
    this.aiJob = {
      id: p.jobId,
      type: AI_GAME_TYPE,
      topic: this.taskForm.value.topic,
      description: this.taskForm.value.description,
      templateId: this.taskForm.value.template.id,
      difficulty: this.taskForm.value.difficulty,
      lang: this.taskForm.value.lang,
      status: p.status,
      progressMsg: p.msg,
      quizId: p.quizId,
      errorCode: p.errorCode
    }
    // update user aiJob
    // console.log('updateStatus aiJob', this.aiJob);
    this.store.dispatch(AuthActions.setUserAIJob({ aiJob: this.aiJob }));
  }

  initState() {
    if (!this.aiJob) return;

    this.started = true;
    this.jobId = this.aiJob.id;
    this.quizStatus = this.aiJob.status;
    this.inQueue = this.aiJob.status === AI_GAME_STATUS.QUEUED;
    this.quizMsg = this.aiJob.progressMsg || '';
    this.completedSuccess = this.aiJob.status === AI_GAME_STATUS.COMPLETED ? true : [AI_GAME_STATUS.FAILED, AI_GAME_STATUS.CANCELED].includes(this.aiJob.status) ? false : null;
    if (this.aiJob.errorDetail) {
      this.errorDetails = JSON.parse(this.aiJob.errorDetail);
      this.debugLog('errorDetails', this.errorDetails);
    }

    this.taskForm.patchValue({
      lang: this.aiJob.lang,
      // template: this.findTemplate(this.aiJob.templateId),
      difficulty: this.aiJob.difficulty,
      topic: this.aiJob.topic,
      description: this.aiJob.description
    });
    if (this.inQueue) {
      this.socketService.trackJobQueue(this.jobId);
    }
  }

  goBack() {
    this.router.navigate(['/playarea']);
  }

  clearState() {
    this.started = false;
    this.jobId = null;
    this.inQueue = null;
    this.queuePosition = 0;
    this.queueEta = 0;
    this.queueSummary = '';
    this.quizStatus = null;
    this.quizMsg = '';
    this.completedSuccess = null;
    this.errorDetails = null;
    this.socketService.untrackJobQueue(this.jobId);

    this.taskForm.patchValue({
      lang: this.currentLang,
      template: '',
      difficulty: '',
      topic: '',
      description: ''
    })
  }

  confirmJob() {
    if (!this.jobId || !this.aiJob) return;

    this.gameBuilderService.confirmAIGameJob(this.jobId).subscribe({
      next: (result) => {
        this.debugLog('SUCCESS', result);
        this.store.dispatch(AuthActions.setUserAIJob({ aiJob: null }));
      },
      error: (error) => {
        this.debugLog('Error confirming AI game:', error);
        this.snackBar.open(
          error.error?.message?? 'Something went wrong',
          'Close',
          { duration: 3000, panelClass: 'errorNotificationMessage' }
        );
      }
    });
  }

  getLangImage(lang: string): string {
    return this.translationService.getLangImage(lang);
  }

  findTemplate(templateId: number) {
    return this.gameTemplates.find(template => template.id === templateId) || null;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.sub.unsubscribe();

    if (this.jobId) {
      // this.confirmJob();
      this.socketService.untrackJobQueue(this.jobId);
    }
  }

  private debugLog(...args: any[]): void {
    if (this.DEBUG) {
      console.log('[QuizAIBaseComponent]', ...args);
    }
  }
}
